import i18n from 'i18next';
import { dynamicObjectType } from 'types';
import { nav } from 'utils';
import { create } from 'zustand';
import useAPIStore from '../APIStore';
import useErrorStore from '../ErrorStore';
import useSystemStore from '../SystemStore';
import useUserStore from '../UserStore';

let ErrorMsgCode = [
	'msg_code_invalid_code', //验证码不匹配
	'msg_code_invalid_auth_code', //授权码不匹配
	'msg_code_authenticated_code', //授权码已被验证
	'msg_code_fail', //普通二维码验证失败
	'msg_code_not_you', //非您接收的礼品
	'msg_code_invalid_gift_code', //赠送授权码不匹配

	'msg_code_AxRQR_ok_invalid_code', //安甄码验证成功，验证码不匹配
	'msg_code_AxRQR_ok_invalid_auth_code', //安甄码验证成功，授权码不匹配
	'msg_code_AxRQR_ok_authenticated_code', //安甄码验证成功，授权码已被验证
	'msg_code_AxRQR_ok_fail', //安甄码验证成功，普通二维码验证失败
	'msg_code_AxRQR_ok_not_you', //安甄码验证成功，非您接收的礼品
	'msg_code_AxRQR_ok_invalid_gift_code', //安甄码验证成功，礼品码不匹配

	'msg_code_AxRQR_stateless_invalid_code', //安甄码不验证，验证码不匹配
	'msg_code_AxRQR_stateless_invalid_auth_code', //安甄码不验证，授权码不匹配
	'msg_code_AxRQR_stateless_authenticated_code', //安甄码不验证，授权码已被验证
	'msg_code_AxRQR_stateless_fail', //安甄码不验证，普通二维码验证失败
	'msg_code_AxRQR_stateless_not_you', //安甄码不验证，非您接收的礼品
	'msg_code_AxRQR_stateless_invalid_gift_code', //安甄码不验证，礼品码不匹配

	'msg_code_noneffective', //无效码
	'msg_code_AxRQR_fail', //安甄码验证失败
	'msg_code_single_suspicious', //单层码安甄码可疑
	'msg_code_single_fail', //单层码安甄码失败
];

interface EntryCodeProps {
	uaid: string | null;
	type: string | null;
}

interface BizLogicStates {
	giftMode: boolean;
	entryCode: EntryCodeProps;
	product: dynamicObjectType | undefined;
	verify: dynamicObjectType | undefined;
}

interface BizLogicActions {
	actions: {
		setGiftMode: (mode: boolean) => void;
		getGiftMode: () => boolean;
		setEntryCode: (entryCode: EntryCodeProps) => void;
		getEntryCode: () => EntryCodeProps;
		processEntryCode: () => void;
		setProduct: (product: dynamicObjectType | undefined) => void;
		resetProduct: () => void;
		setVerify: (verify: dynamicObjectType | undefined) => void;
		mapRedemptionStatus: (status: number) => string;
		resetVerify: () => void;
		datetimeConversion: (
			datetimeData: number,
			onlyDate?: boolean
		) => string;
		getAuthBannerDate: (
			scanData: dynamicObjectType | undefined
		) => string | void;
		handleQueryResponse: (res: dynamicObjectType) => void;
		handleAxRCodeInfo: (res: dynamicObjectType, uaid: string) => void;
	};
}

const useBizLogicStore = create<BizLogicStates & BizLogicActions>(
	(set, get) => ({
		giftMode: false,
		entryCode: { uaid: null, type: null },
		product: undefined,
		verify: undefined,
		actions: {
			setGiftMode: (mode) => {
				set(() => ({
					giftMode: mode,
				}));
			},
			getGiftMode: () => {
				return get().giftMode;
			},
			setEntryCode: (entryCode) => {
				set(() => ({
					entryCode: entryCode,
				}));
			},
			getEntryCode: () => {
				return get().entryCode;
			},
			processEntryCode: async () => {
				const entryCode = get().entryCode;
				if (!entryCode.uaid) return;
				get().actions.setEntryCode({
					uaid: null,
					type: null,
				});
				nav('/ProductDetail');
				switch (entryCode.type) {
					case '1':
						const ownershipCardInfo = useUserStore.getState()
							.userToken
							? null
							: localStorage.getItem(entryCode.uaid);
						const signature = ownershipCardInfo
							? JSON.parse(ownershipCardInfo).signature
							: null;
						get().actions.handleQueryResponse(
							await useAPIStore
								.getState()
								.api.authenticateUAID(
									entryCode.uaid,
									signature,
									null,
									null,
									null,
									!!signature
								)
						);
						break;
					case '2':
						get().actions.handleAxRCodeInfo(
							await useAPIStore
								.getState()
								.api.getUAIDInfo(entryCode.uaid),
							entryCode.uaid
						);
						break;
				}
			},
			setProduct: (product) => set(() => ({ product: product })),
			resetProduct: () => set(() => ({ product: {} })),
			setVerify: (verify) => set(() => ({ verify: verify })),
			mapRedemptionStatus: (status) => {
				switch (status) {
					case 1:
						return i18n.t('redemption.status.1');
					case 2:
						return i18n.t('redemption.status.2');
					case 4:
						return i18n.t('redemption.status.4');
					default:
						return i18n.t('redemption.status.unknown');
				}
			},
			resetVerify: () => set(() => ({ verify: {} })),
			datetimeConversion: (datetimeData, onlyDate = false) => {
				const locale =
					i18n.resolvedLanguage === 'en_US' ? 'en-SG' : 'zh-CN';
				let option: dynamicObjectType = onlyDate
					? {
							year: 'numeric',
							month: 'long',
							day: 'numeric',
					  }
					: {
							year: 'numeric',
							month: 'long',
							day: 'numeric',
							hour: '2-digit',
							minute: '2-digit',
							second: '2-digit',
							hour12: false,
					  };
				return new Date(datetimeData).toLocaleDateString(
					locale,
					option
				);
			},
			getAuthBannerDate: (scanData) => {
				if (!scanData) return;
				let authBannerTimestamp;
				switch (process.env.REACT_APP_AUTHBANNERDATE_MODE) {
					case '1':
						authBannerTimestamp = scanData.firstScanAt;
						break;
					case '2':
						authBannerTimestamp = scanData.lastScanAt;
						break;
					case '3':
						authBannerTimestamp = scanData.verifyAt;
						break;
					case '4':
						authBannerTimestamp = scanData.lastVerifyAt;
						break;
					default:
						return;
				}
				if (!authBannerTimestamp) return;
				return get().actions.datetimeConversion(authBannerTimestamp);
			},
			handleQueryResponse: (res) => {
				console.log(res);
				console.log(parseInt(res.code) === 0);
				if (!(parseInt(res.code) === 0)) {
					useErrorStore.getState().actions.handleQueryError(res);
					return;
				}
				let data = res.data,
					//verify = data.verify || {}, no such verify thing for the new API
					authMessageExt = data.authMessageExt,
					authenticationMessage = data.authenticationMessage,
					product = data.productBatch, //product
					//ownerList = data.ownerList, no such thing
					//ownerList = data.ownerHistory,
					//message = verify.msg.length ? JSON.parse(verify.msg) : '',
					/*message = data.authenticationMessage ? data.authenticationMessage.message : undefined,*/
					//ownerShip = data.ownerShip,
					ownershipData = data.ownershipData,
					//hiddenProductInfo = data.hiddenProductInfo; put asside first
					verifyStatus = data.authMessageExt.verifyStatus,
					verifyAxRStatus = data.authMessageExt.verifyAxRStatus,
					axrImgVerifyErrorCode =
						authMessageExt.axrImgVerifyErrorCode,
					recallInfo: Array<dynamicObjectType> = data.recallInfo,
					challenge = data.challenge,
					//there is a new type means 1: Dual Layers (Exposed and Hidden Code) 2: Authentication Code 3: Authorization Code 4: Package Code 5: Single Layer Code
					uaid = data.uaid;
				/*,
					currentuaidlayer = uaid.layerNumberInSpecs,
					uaidType = uaid.specs.layers[currentuaidlayer - 1].type, //1: Exposed code, 2: Hidden code, 3: Package code, 4: Authorization code, 5: Authentication code, 6: Exposed code of authorization code, 7: Single layer code
					qrCodeType = uaid.specs.layers[currentuaidlayer - 1].format //1: QR Code, 2: AR Code, 3: Text
					*/

				if (axrImgVerifyErrorCode === '130095') {
					useErrorStore
						.getState()
						.actions.showError(130095, 'error.notSupport', {});
					return;
				}

				if (axrImgVerifyErrorCode === 'ARCODE.K8S.ERR.11003') {
					useErrorStore
						.getState()
						.actions.showError(11003, 'error.notGenuine', {});
					return;
				}

				if (axrImgVerifyErrorCode === 'ARCODE.K8S.ERR.41') {
					useErrorStore
						.getState()
						.actions.showError(11003, 'error.notGenuine', {});
					return;
				} else if (axrImgVerifyErrorCode === 'ARCODE.SDK.ERR.25') {
					useErrorStore
						.getState()
						.actions.showError(12025, 'error.tilt', {});
					return;
				} else if (axrImgVerifyErrorCode === 'ARCODE.SDK.ERR.29') {
					useErrorStore
						.getState()
						.actions.showError(12029, 'error.blur', {});
					return;
				}

				let ownerHistory =
					data.ownerHistory.length > 0
						? data.ownerHistory.map((item: dynamicObjectType) => {
								let onlyDate = get().actions.datetimeConversion(
									item.effectiveAt,
									true
								);
								let dateTime = get().actions.datetimeConversion(
									item.effectiveAt
								);

								return {
									username: item.username,
									displayName: item.displayName,
									avatarImagePath: item.avatarImagePath,
									effectiveAt: item.effectiveAt,
									blockchainTransactionId:
										item.blockchainTransactionId,
									effectiveAtOnlyDate: onlyDate,
									effectiveAtDateTime: dateTime,
								};
						  })
						: [];

				let hiddenProductInfoList = data.hiddenProductInfoList
					? data.hiddenProductInfoList.map(
							(item: dynamicObjectType) => {
								let imageLink = useSystemStore
									.getState()
									.actions.resolveMultiLang(item.imageLink);
								let message = useSystemStore
									.getState()
									.actions.resolveMultiLang(item.message);

								return {
									image: item.image,
									imageLink: imageLink,
									euaid: item.euaid,
									message: message,
									huaid: item.huaid,
									order: item.order,
								};
							}
					  )
					: [];

				let firstScanDatetime = data.scanData
					? get().actions.datetimeConversion(
							data.scanData.firstScanAt
					  )
					: i18n.t('default.no.data');

				let lastScanDatetime = data.scanData
					? get().actions.datetimeConversion(data.scanData.lastScanAt)
					: i18n.t('default.no.data');

				let firstVerifySDatetime = data.scanData
					? get().actions.datetimeConversion(data.scanData.verifyAt)
					: i18n.t('default.no.data');

				let lastVerifySDatetime = data.scanData
					? get().actions.datetimeConversion(
							data.scanData.lastVerifyAt
					  )
					: i18n.t('default.no.data');

				let firstVerifySDatetimeInt = data.scanData
					? data.scanData.verifyAt
					: i18n.t('default.no.data');

				const totalScanCount = data.scanData
					? data.scanData.totalScanCount
					: '0';

				const authenticationMessageObj = JSON.parse(
					useSystemStore
						.getState()
						.actions.resolveMultiLang(
							authenticationMessage.message
						) || '{}'
				);

				//handle recall info, convert time data to readable format
				//	This logic shouldn't be handled here, as we can convert datetime at RecallInfoBox.
				// 	Comment out this part of code as it can overwrite original data.
				// 	Note: Keeping original data as state is better than keeping a processed data.
				// if (recallInfo && recallInfo.length > 0) {
				// 	for (data of recallInfo) {
				// 		data['startTime'] = get().actions.datetimeConversion(
				// 			data['startTime'],
				// 			true
				// 		);
				// 	}
				// }

				//handle ownership
				if (ownershipData != null && ownershipData !== undefined) {
					console.log('store ownership data');
					let ownershipDataInString: string =
						JSON.stringify(ownershipData);
					let euaid = ownershipData.euaid;
					console.log('Expose Code: ' + euaid);
					let huaid = ownershipData.huaid;
					console.log('Hidden Code: ' + huaid);
					localStorage.setItem(euaid, ownershipDataInString);
					localStorage.setItem(huaid, ownershipDataInString);
				}

				let productBatchName, //init the message
					uaidType,
					qrCodeType;

				if (product) {
					let imgs = product.images
						? product.images
								.filter((item: dynamicObjectType) => {
									//return item.isShow === 1;
									return item.displayFlag === true;
								})
								.map((item: dynamicObjectType) => {
									//return item.img;
									return item.imagePath;
								})
						: [];
					//fields = product.txt
					let fields = product.attributes
						.filter((item: dynamicObjectType) => {
							// return item.isShow === 1;
							return item.displayFlag === true;
						})
						.map((item: dynamicObjectType) => {
							if (item.code === 'name') {
								//here special handlng to get the product batch name to use as product name.
								productBatchName = item.value;
							}
							return {
								//name: JSON.parse(item.name),
								//value: JSON.parse(item.value),
								name: item.name,
								value: item.value,
								valueType: item.valueType,
								fieldType: item.fieldType,
								code: item.code,
							};
						}); //,
					//productId = product.productId; no sunch thing

					product = {
						//name: JSON.parse(product.productName),
						name: product.productName,
						imgs: imgs,
						fields: fields,
						productBatchName: productBatchName,
						pointMsg: data.pointMsg,
						//prodId: productId, no sunch thing
						//uaidID: verify.uaidId, no sunch thing
						//uaidType: verify.uaidType,
						//uaidType: uaidType,
						//thirdPartyId: thirdPartyId, no such thing
						//uaidSerialNum: data.uaidInfo.createNum.toString(), not need any more
						//uaidBatchNo: data.uaidInfo.uaidBatchNo.toString(), put aside first
						//currentReqUAID: verify.reqCode, no such thing
						//currentReqUAID: uaid.uaid,
						recallInfo: recallInfo,
						//batchNumber: uaid.uaidBatch.batchNumber,
						authenticationId: data.authenticationId,
						challengeCode: challenge
							? challenge.challengeCode
							: undefined,
						ownerHistory: ownerHistory,
						hiddenProductInfoList: hiddenProductInfoList,
					};
				}

				/*
				if (verifyStatus === 1 && verifyAxRStatus === 0) {
					//console.log(verifyStatus, verifyAxRStatus, data.authMessageExt.axrImgVerifyErrorCode?data.authMessageExt.axrImgVerifyErrorCode:undefined)
					if (
						data.authMessageExt.axrImgVerifyErrorCode === '130095'
					) {
						qrCodeType = 2;
						//model is not support - T value not updated
						message = {
							zh_CN: {
								title: '',
								content: '此手机不支持实时防伪认证功能',
							},
							zh_TW: {
								title: '',
								content: '此手機不支持實時防偽認證功能',
							},
							en_US: {
								title: '',
								content:
									'This phone does not support real-time anti-counterfeiting authentication',
							},
						};
						//console.log(data.authMessageExt.axrImgVerifyErrorCode)

						useErrorStore
							.getState()
							.actions.showError(
								130095,
								'This phone does not support real-time anti-counterfeiting authentication',
								{}
							);
					} else {
						console.log(data.authMessageExt);
						message = {
							zh_CN: {
								title: '',
								content:
									data.authMessageExt.axrImgVerifyErrorCode,
							},
							zh_TW: {
								title: '',
								content:
									data.authMessageExt.axrImgVerifyErrorCode,
							},
							en_US: {
								title: '',
								content:
									data.authMessageExt.axrImgVerifyErrorCode,
							},
						};
						useErrorStore
							.getState()
							.actions.showError(
								data.authMessageExt.axrImgVerifyErrorCode,
								data.authMessageExt.axrImgVerifyErrorCode,
								{}
							);
					}
				} /*else if (verifyStatus === 1 && verifyAxRStatus === 2) {
				if (
					data.authenticationMessage.code ===
					'msg_code_AxRQR_ok_wait_hide_code'
				) {
					message = data.authenticationMessage.message;
					uaidType = 1;
					qrCodeType = 2;

					product['uaidType'] = uaidType;
					product['currentReqUAID'] = undefined;
					product['batchNumber'] = undefined;
					setProduct(product);

					setVerify({
						status: 'verify',
						lastScanDatetime: lastScanDatetime,
						verifyStatus: verifyStatus,
					});
					navigate('/Product');
				}
			} else {
			}*/
				let currentuaidlayer = uaid.layerNumberInSpecs;
				uaidType = uaid.specs.layers[currentuaidlayer - 1].type;
				qrCodeType = uaid.specs.layers[currentuaidlayer - 1].format;

				product['uaidType'] = uaidType;
				product['currentReqUAID'] = uaid.uaid;
				product['batchNumber'] = uaid.uaidBatch.batchNumber;
				product['qrCodeType'] = qrCodeType;

				get().actions.setProduct(product);

				//	For ARCode, verify status is unknown or failed, check axrImgVerifyErrorCode for microservice error
				if (
					qrCodeType === 2 &&
					(verifyAxRStatus === 0 || verifyAxRStatus === 1)
				) {
					useErrorStore
						.getState()
						.actions.showError(-1, 'app.error.unknown', {});
					return;
				}
				/*
				let currentuaidlayer = uaid.layerNumberInSpecs;
				uaidType = uaid.specs.layers[currentuaidlayer - 1].type;
				qrCodeType = uaid.specs.layers[currentuaidlayer - 1].format;

				product['uaidType'] = uaidType;
				product['currentReqUAID'] = uaid.uaid;
				product['batchNumber'] = uaid.uaidBatch.batchNumber;
				product['qrCodeType'] = qrCodeType;

				get().actions.setProduct(product);
                */
				//	针对送礼码开后门
				if (
					authenticationMessage.code &&
					authenticationMessage.code.includes('wait_gift_code')
				) {
					console.log(
						'gift mode',
						authenticationMessage.code,
						verifyAxRStatus,
						verifyStatus
					);
					get().actions.setVerify({
						status: 'verify',
						scanData: data.scanData,
						firstScanDatetime: firstScanDatetime,
						lastScanDatetime: lastScanDatetime,
						firstVerifySDatetime: firstVerifySDatetime,
						lastVerifySDatetime: lastVerifySDatetime,
						firstVerifySDatetimeInt: firstVerifySDatetimeInt,
						totalScanCount: totalScanCount,
						verifyStatus: verifyStatus,
						code: authenticationMessage.code,
						message: authenticationMessageObj,
					});
					nav('/Product', { replace: true });
					//	针对召回码开后门
				} else if (
					authenticationMessage.code &&
					authenticationMessage.code.includes('recall')
				) {
					console.log(
						'recall mode',
						authenticationMessage.code,
						verifyAxRStatus,
						verifyStatus
					);
					get().actions.setVerify({
						status: 'failed',
						scanData: data.scanData,
						firstScanDatetime: firstScanDatetime,
						lastScanDatetime: lastScanDatetime,
						firstVerifySDatetime: firstVerifySDatetime,
						firstVerifySDatetimeInt: firstVerifySDatetimeInt,
						lastVerifySDatetime: lastVerifySDatetime,
						totalScanCount: totalScanCount,
						verifyStatus: verifyStatus,
						code: authenticationMessage.code,
						message: authenticationMessageObj,
					});
					nav('/Product', { replace: true });
				} else if (
					(uaidType === 4 &&
						[undefined, true, true, false, false][verifyStatus]) ||
					(qrCodeType === 2 &&
						[true, true, undefined, true, true, true][
							verifyAxRStatus
						]) ||
					verifyStatus === 2 ||
					(authenticationMessage.code.length &&
						ErrorMsgCode.find(function (msgCode) {
							return msgCode === authenticationMessage.code;
						})) //针对授权码和送礼情况的条件判断，当msgCode不是等待验证，成功，重复验证的情况下显示错误页面
				) {
					// App.setState({message:message[App._lang].content})
					if (
						//(verify.qrCodeType == 2 &&
						(qrCodeType === 2 &&
							[true, true, undefined, true, true, true][
								verifyAxRStatus
							]) ||
						(authenticationMessage.code.length &&
							ErrorMsgCode.find(function (msgCode) {
								return msgCode === authenticationMessage.code;
							}))
					) {
						if (
							[
								'msg_code_successful',
								'msg_code_repetitive_self',
								'msg_code_AxRQR_ok_successful',
								'msg_code_AxRQR_stateless_repetitive_self',
							].indexOf(authenticationMessage.code) !== -1
						) {
							console.log('Case 0: verification success');
							get().actions.setVerify({
								status: 'success',
								scanData: data.scanData,
								firstScanDatetime: firstScanDatetime,
								lastScanDatetime: lastScanDatetime,
								firstVerifySDatetime: firstVerifySDatetime,
								lastVerifySDatetime: lastVerifySDatetime,
								firstVerifySDatetimeInt:
									firstVerifySDatetimeInt,
								totalScanCount: totalScanCount,
								verifyStatus: verifyStatus,
								code: authenticationMessage.code,
								message: authenticationMessageObj,
							});
							nav('/Product', { replace: true });
						} else if (
							[
								'msg_code_fail',
								'msg_code_hidden_authed_fail',
								'msg_code_AxRQR_ok_fail',
								'msg_code_AxRQR_stateless_fail',
							].indexOf(authenticationMessage.code) !== -1
						) {
							console.log('Case 1: verification fail');
							get().actions.setVerify({
								status: 'failed',
								scanData: data.scanData,
								firstScanDatetime: firstScanDatetime,
								lastScanDatetime: lastScanDatetime,
								firstVerifySDatetime: firstVerifySDatetime,
								lastVerifySDatetime: lastVerifySDatetime,
								firstVerifySDatetimeInt:
									firstVerifySDatetimeInt,
								totalScanCount: totalScanCount,
								verifyStatus: verifyStatus,
								code: authenticationMessage.code,
								message: authenticationMessageObj,
							});
							nav('/Product', { replace: true });
						} else {
							console.log('Case 2: verification error');
							useErrorStore
								.getState()
								.actions.showError(
									-1,
									authenticationMessageObj.content,
									{}
								);
						}
					} else {
						console.log(
							'3',
							authenticationMessage.code,
							verifyAxRStatus,
							verifyStatus
						);
						get().actions.setVerify({
							status: 'failed',
							scanData: data.scanData,
							firstScanDatetime: firstScanDatetime,
							lastScanDatetime: lastScanDatetime,
							firstVerifySDatetime: firstVerifySDatetime,
							firstVerifySDatetimeInt: firstVerifySDatetimeInt,
							lastVerifySDatetime: lastVerifySDatetime,
							totalScanCount: totalScanCount,
							verifyStatus: verifyStatus,
							code: authenticationMessage.code,
							message: authenticationMessageObj,
						});
						nav('/Product', { replace: true });
					}
					//App.setState({requestCode:verify.reqCode}
				} else {
					if (
						product &&
						//(verify.uaidType == 7 ||
						(uaidType === 7 ||
							//(verify.verifyStatus == 1 && verify.verifyAxRStatus == 2) ||
							(verifyStatus === 1 && verifyAxRStatus === 2) ||
							//(verify.verifyStatus == 1 && verify.verifyAxRStatus == 0))
							(verifyStatus === 1 && verifyAxRStatus === 0))
					) {
						//if (verify.msgCode === 'msg_code_successful') {
						if (
							[
								'msg_code_successful',
								'msg_code_single_success',
								'msg_code_single_ordinary',
							].indexOf(authenticationMessage.code) !== -1
						) {
							//alert('verification success for hidden code');
							console.log(
								'4',
								authenticationMessage.code,
								verifyAxRStatus,
								verifyStatus
							);
							get().actions.setVerify({
								status: 'success',
								scanData: data.scanData,
								firstScanDatetime: firstScanDatetime,
								lastScanDatetime: lastScanDatetime,
								firstVerifySDatetime: firstVerifySDatetime,
								lastVerifySDatetime: lastVerifySDatetime,
								firstVerifySDatetimeInt:
									firstVerifySDatetimeInt,
								totalScanCount: totalScanCount,
								verifyStatus: verifyStatus,
								code: authenticationMessage.code,
								message: authenticationMessageObj,
							});
							nav('/Product', { replace: true });
							// App.go('result')
						} else {
							// alert("verification success for exposed code")
							console.log(
								'5',
								authenticationMessage.code,
								verifyAxRStatus,
								verifyStatus
							);
							get().actions.setVerify({
								status: 'verify',
								scanData: data.scanData,
								firstScanDatetime: firstScanDatetime,
								lastScanDatetime: lastScanDatetime,
								firstVerifySDatetime: firstVerifySDatetime,
								lastVerifySDatetime: lastVerifySDatetime,
								firstVerifySDatetimeInt:
									firstVerifySDatetimeInt,
								totalScanCount: totalScanCount,
								verifyStatus: verifyStatus,
								code: authenticationMessage.code,
								message: authenticationMessageObj,
							});
							nav('/Product', { replace: true });
							// App.go('product')
						}
					} else {
						//alert('verification success for hidden code');
						console.log(
							'6',
							authenticationMessage.code,
							verifyAxRStatus,
							verifyStatus
						);
						get().actions.setVerify({
							status: 'success',
							scanData: data.scanData,
							firstScanDatetime: firstScanDatetime,
							lastScanDatetime: lastScanDatetime,
							firstVerifySDatetime: firstVerifySDatetime,
							lastVerifySDatetime: lastVerifySDatetime,
							firstVerifySDatetimeInt: firstVerifySDatetimeInt,
							totalScanCount: totalScanCount,
							verifyStatus: verifyStatus,
							code: authenticationMessage.code,
							message: authenticationMessageObj,
						});
						nav('/Product', { replace: true });
						// App.go('result')
					}
				}
				//}

				/* move ahead
				if (product) {
					let imgs = product.images
							.filter((item: dynamicObjectType) => {
								//return item.isShow === 1;
								return item.displayFlag === true;
							})
							.map((item: dynamicObjectType) => {
								//return item.img;
								return item.imagePath;
							}),
						//fields = product.txt
						fields = product.attributes
							.filter((item: dynamicObjectType) => {
								return item.isShow === 1;
							})
							.map((item: dynamicObjectType) => {
								return {
									//name: JSON.parse(item.name),
									//value: JSON.parse(item.value),
									name: item.name,
									value: item.value,
									valueType: item.valueType,
									code: item.code,
								};
							})//,
						//productId = product.productId; no sunch thing

					product = {
						//name: JSON.parse(product.productName),
						name: product.productName,
						imgs: imgs,
						fields: fields,
						//prodId: productId, no sunch thing
						//uaidID: verify.uaidId, no sunch thing
						//uaidType: verify.uaidType,
						uaidType: uaidType,
						//thirdPartyId: thirdPartyId, no such thing
						//uaidSerialNum: data.uaidInfo.createNum.toString(), not need any more
						//uaidBatchNo: data.uaidInfo.uaidBatchNo.toString(), put aside first
						//currentReqUAID: verify.reqCode, no such thing
						currentReqUAID: uaid.uaid,
						recallInfo: recallInfo,
						batchNumber: uaid.uaidBatch.batchNumber,
						authenticationId: data.authenticationId,
						challengeCode: challenge?challenge.challengeCode:undefined
					};
				}
				*/

				/* put aside first
				if (ownerList) {
					ownerList = ownerList.map((item: dynamicObjectType) => {
						try {
							item.companyName = JSON.parse(item.compName);
						} catch (err) {
							item.companyName = item.compName;
						}
						return item;
					});
				}
				*/

				/* put aside
				if (message) {
					for (let key in message) {
						try {
							message[key] = JSON.parse(message[key].trim());
						} catch (err) {
							console.log('get error at' + key);
							console.log(err);
						}
					}

					if (!message[_lang]) {
						message[_lang] = message['en_US'];
					}
				}*/

				/* // move the basic part to ahead
				if (ownershipData != null && ownershipData !== undefined) { //the way to handle the ownership may need to change since the api change
					let ownershipDataInString:string = JSON.stringify(ownershipData);
					let euaid = ownershipData.euaid;
					let huaid = ownershipData.huaid;
					localStorage.setItem(euaid, ownershipDataInString);
					localStorage.setItem(huaid, ownershipDataInString);

					console.log("some special handling about shorter and normal url")

					//check whether there is a short URL for the request code
					if (data.uaidInfo.argotShortUrl != '') {
						let list = data.uaidInfo.argotShortUrl.split('/');
						localStorage.setItem(
							list[list.length - 1],
							JSON.stringify(ownerShip)
						);
						localStorage.setItem(
							list[list.length - 1] + '_linkto',
							data.uaidInfo.argotCode
						); //in order to clear all, so create this mapping
						//App.setState({argotIsShort: true})
					}

					if (data.uaidInfo.argotCode !== '') {
						localStorage.setItem(
							data.uaidInfo.argotCode,
							JSON.stringify(ownerShip)
						);
					}

					//check whether there is a short URL for the othere pair of the request code
					if (data.uaidInfo.uaidShortUrl != '') {
						let list = data.uaidInfo.uaidShortUrl.split('/');
						localStorage.setItem(
							list[list.length - 1],
							JSON.stringify(ownerShip)
						);
						localStorage.setItem(
							list[list.length - 1] + '_linkto',
							data.uaidInfo.uaidCode
						); // in order to clear all, so create this mapping
						//App.setState({uaidIsShort: true})
					}

					if (data.uaidInfo.uaidCode != '') {
						localStorage.setItem(
							data.uaidInfo.uaidCode,
							JSON.stringify(ownerShip)
						);
					}



					//this is not the best way to handle short url for authorization issue, maybe nned to discuss
					//if (verify.uaidType == 4) {
					if (uaidType === 4) {
						console.log("special handling for authorization code, since it's 'hidden' code has no info of the exposure code")

						//special handling for authorization code, since it's 'hidden' code has no info of the exposure code
						let exposed_code = ownerShip.euaid;
						if (exposed_code.includes('http')) {
							let list = exposed_code.split('/');
							exposed_code = list[list.length - 1];
						}

						localStorage.setItem(exposed_code, JSON.stringify(ownerShip));
						let other_expose_code = localStorage.getItem(
							exposed_code + '_linkto'
						);
						if (other_expose_code) {
							localStorage.setItem(
								other_expose_code,
								JSON.stringify(ownerShip)
							);
							localStorage.removeItem(other_expose_code + '_linkto');
						}

					}
				} else {
					if (
						//verify.uaidType == 6 &&
						uaidType === 6 &&
						//(verify.verifyStatus == 1 || verify.verifyAxRStatus == 2)
						(verifyStatus === 1 || verifyAxRStatus === 2)
					) {
						console.log("special handling for authorization code, since it's expose code possible with short url")

						//special handling for authorization code, since it's expose code possible with short url  --> need to restructure it or need change in API
						let list = data.uaidInfo.uaidShortUrl.split('/');
						localStorage.setItem(
							list[list.length - 1] + '_linkto',
							data.uaidInfo.uaidCode
						);
						localStorage.setItem(
							data.uaidInfo.uaidCode + '_linkto',
							list[list.length - 1]
						);
						//in order to display exposure authorization code's batch for the first time scan the hidden one
						//store it in in localstorage
						localStorage.setItem(
							'author_batch',
							data.uaidInfo.uaidBatchNo.toString()
						);
						localStorage.setItem(
							'author_serial',
							data.uaidInfo.createNum.toString()
						);

						let uaidSerialNum = data.uaidInfo.createNum.toString();
						if (uaidSerialNum.length < 8) {
							let left = 8 - uaidSerialNum.length;
							uaidSerialNum = '0'.repeat(left) + uaidSerialNum;
						}

						let thirdPartyId = data.uaidInfo.thirdId;
						if (thirdPartyId == '') {
							thirdPartyId =
								'B' +
								data.uaidInfo.uaidBatchNo.toString() +
								'-S' +
								uaidSerialNum;
						}

						localStorage.setItem('author_third_id', thirdPartyId);
					}
				}
				*/

				/* //moved to ahead
				//	ARCode verification status is unknown or failed
				if (
					//verify.qrCodeType === 2 &&
					//(verify.verifyAxRStatus === 0 || verify.verifyAxRStatus === 1)
					qrCodeType === 2 &&
					(verifyAxRStatus === 0 || verifyAxRStatus === 1)
				) {
					if (data.authMessageExt.axrImgVerifyErrorCode === '130095') {
						//model is not support - T value not updated
						message = {
							zh_CN: {
								title: '',
								content: '此手机不支持实时防伪认证功能',
							},
							zh_TW: {
								title: '',
								content: '此手機不支持實時防偽認證功能',
							},
							en_US: {
								title: '',
								content:
									'This phone does not support real-time anti-counterfeiting authentication',
							},
						};
					} else {
						message = {
							zh_CN: { title: '', content: data.axrImgVerifyMsg },
							zh_TW: { title: '', content: data.axrImgVerifyMsg },
							en_US: { title: '', content: data.axrImgVerifyMsg },
						};
					}
					if (
						[
							'ARCODE.SDK.ERR.1',
							'ARCODE.SDK.ERR.22',
							'ARCODE.SDK.ERR.29',
						].indexOf(data.axrImgVerifyErrorCode) !== -1 &&
						//['1', '6'].indexOf(verify.uaidType) !== -1
						['1', '6'].indexOf(uaidType) !== -1
					) {
						//	When the uaid is expose code(1 or 6), and scanning failed due to camera issue, we need to provide scan hidden code option.
						//	Set state to show scan hidden code button.
					}
				}*/

				/*About wx offical account verify, put aside first
				//let reqCodeForRecallInfo = verify.reqCode;
				let reqCodeForRecallInfo = uaid.uaid;
				let wxVerifyRecallInfo = [];

				//if (verify.uaidType == 4 && verify.verifyStatus == 3) {
				if (uaidType ===4 && verifyStatus === 3) {
					//if (ownerShip) {
					if (ownershipData) {
						//reqCodeForRecallInfo = ownerShip.euaid; //special handling for the scanning on authorization code for reacall info, since authorization code is not able to get the recall info,
						reqCodeForRecallInfo = ownershipData.euaid;
						console.log(
							'reqCodeForRecallInfo change to -> ' + reqCodeForRecallInfo
						); //so need to use the exposure code to get the recall info
					} else {
						//special handling about wxverify, it can't get ownership info
						console.log('using hisRecallInfo from vxVerify');
						// here put aside first,
						wxVerifyRecallInfo =
							verify.hisRecallInfo === undefined ||
							verify.hisRecallInfo === '' ||
							verify.hisRecallInfo === null ||
							(verify.hisRecallInfo &&
								verify.hisRecallInfo.length === 1 &&
								!('id' in Object.keys(verify.hisRecallInfo[0])))
								? []
								: verify.hisRecallInfo; //special handling of the {ended:false, status:0} return
						if (wxVerifyRecallInfo.length > 0) {
							for (data of wxVerifyRecallInfo) {
								data['startTime'] = new Date(
									data['startTime']
								).toLocaleDateString('sv-se'); //yyyy-mm-dd
							}
						}
					}
				}*/

				// setProduct({
				// 	product:product,
				// 	verify:{
				// 		uaidType:verify.uaidType,//uaid类型 {0:'不定',1:'明码',2:'暗码',3:'包装码',4:'授权码',5:'认证码',6:'授权码明码',7:'单层码'}
				// 		codeType: verify.qrCodeType,//二维码类型 {1:'普通码',2:'安甄码'}
				// 		verifyStatus: verify.verifyStatus,//验证状态 {1:'未知',2:'失败',3:'成功',4:'本人重复验证'}
				// 		AxRStatus: verify.verifyAxRStatus,//安甄码验证状态 {0:'未知',1:'失败',2:'成功',3:'未验证',4:'可疑'}
				// 		fromVerifyId:verify.fromVerifyId,
				// 		fromChannel:verify.fromChannel,
				// 		reqCode:verify.reqCode,
				// 		longUrl:verify.longUrl,
				// 		message:message,
				// 		//recallStatus: verify.recallStatus, //? verify.recallStatus:null,
				// 		recallStatus: verify.recallStatus,
				// 		// recallInfo: wxVerifyRecallInfo.length>0?wxVerifyRecallInfo:res_.data,
				// 		// fromPage:history?'history':'other'
				// 	},
				// 	ownerList:ownerList,
				// 	requestCode:verify.reqCode,
				// 	companyCode:verify.companyCode,
				// 	productName:verify.productName,
				// 	hiddenProductInfo:hiddenProductInfo
				// 	// companyCode:verify.companyCode
				// })

				//setProduct(product); move ahead

				/* move ahead
				if (
					(uaidType === 4 &&
						[undefined, true, true, false, false][verifyStatus]) ||
					(qrCodeType === 2 &&
						[true, true, undefined, true, true][verifyAxRStatus]) ||
					verifyStatus === 2 ||
					(authenticationMessage.code.length &&
						ErrorMsgCode.find(function (msgCode) {
							return msgCode === authenticationMessage.code;
						})) //针对授权码和送礼情况的条件判断，当msgCode不是等待验证，成功，重复验证的情况下显示错误页面
				) {
					// App.setState({message:message[App._lang].content})
					if (
						//(verify.qrCodeType == 2 &&
						(qrCodeType === 2 &&
							[true, true, undefined, true, true][
								verifyAxRStatus
							]) ||
						(authenticationMessage.code.length &&
							ErrorMsgCode.find(function (msgCode) {
								return msgCode === authenticationMessage.code;
							}))
					) {
						if (
							authenticationMessage.code === 'msg_code_fail' ||
							authenticationMessage.code === 'msg_code_hidden_authed_fail' ||
							authenticationMessage.code === 'msg_code_AxRQR_ok_successful' ||
							authenticationMessage.code === 'msg_code_AxRQR_ok_fail' ||
							authenticationMessage.code === 'msg_code_AxRQR_stateless_fail'
						) {
							// App.go('result')
							console.log("1", authenticationMessage.code, verifyAxRStatus, verifyStatus);
							setVerify({status:"failed",lastScanDatetime:lastScanDatetime, verifyStatus:verifyStatus})
							navigate('/Product');

							//alert(
								//'verification failed for hidden code - check ownershipcard'
							);
						} else {
							console.log("2", authenticationMessage.code, verifyAxRStatus, verifyStatus);
							navigate('/Error')
						}
					} else {
						console.log("3", authenticationMessage.code, verifyAxRStatus, verifyStatus);
						setVerify({status:"failed",lastScanDatetime:lastScanDatetime, verifyStatus:verifyStatus})
						navigate('/Product');
					}
					//App.setState({requestCode:verify.reqCode}
				} else {
					if (
						product &&
						//(verify.uaidType == 7 ||
						(uaidType === 7 ||
							//(verify.verifyStatus == 1 && verify.verifyAxRStatus == 2) ||
							(verifyStatus === 1 && verifyAxRStatus === 2) ||
							//(verify.verifyStatus == 1 && verify.verifyAxRStatus == 0))
							(verifyStatus === 1 && verifyAxRStatus === 0))
					) {
						//if (verify.msgCode === 'msg_code_successful') {
						if (authenticationMessage.code === 'msg_code_successful') {
							//alert('verification success for hidden code');
							console.log("4", authenticationMessage.code, verifyAxRStatus, verifyStatus);
							setVerify({status:"success",lastScanDatetime:lastScanDatetime, verifyStatus:verifyStatus})
							navigate('/Product');
							// App.go('result')
						} else {
							// alert("verification success for exposed code")
							console.log("5", authenticationMessage.code, verifyAxRStatus, verifyStatus);
							setVerify({status:"verify",lastScanDatetime:lastScanDatetime, verifyStatus:verifyStatus})
							navigate('/Product');
							// App.go('product')
						}
					} else {
						//alert('verification success for hidden code');
						console.log("6", authenticationMessage.code, verifyAxRStatus, verifyStatus);
						setVerify({status:"success",lastScanDatetime:lastScanDatetime, verifyStatus:verifyStatus})
						navigate('/Product');
						// App.go('result')
					}
				}*/
			},
			handleAxRCodeInfo: (res, uaid) => {
				console.log(res);
				console.log(parseInt(res.code) === 0);
				if (!(parseInt(res.code) === 0)) {
					console.log('here we go error');
					useErrorStore.getState().actions.handleQueryError(res);
					return;
				}
				if (!res.data.productBatch) {
					console.log('code have no binding');
					useErrorStore
						.getState()
						.actions.showError(1004012, 'verify.notbinded');
					return;
				}
				const hiddenCodeUsed = res.data.uaid.isVerify;
				console.log('hidden code used: ' + hiddenCodeUsed);
				if (hiddenCodeUsed) {
					console.log('Hidden code is used');
					let signature = null;
					if (!useUserStore.getState().userToken) {
						console.log('Anonymous, check local storage');
						let ownershipCardInfo = localStorage.getItem(uaid);
						signature = ownershipCardInfo
							? JSON.parse(ownershipCardInfo).signature
							: null;
					}
					//handleQueryResponse(await authenticateUAID(error.data.UAIDCode, signature, null, null, null))
					useAPIStore
						.getState()
						.api.authenticateUAID(
							uaid,
							signature,
							null,
							null,
							null,
							true
						)
						.then((res) => {
							get().actions.handleQueryResponse(res);
						})
						.catch((error) => {
							let errorObject = JSON.parse(error.message);
							useErrorStore
								.getState()
								.actions.showError(
									errorObject.code,
									errorObject.message,
									{}
								);
						});
				} else {
					let product = res.data.productBatch;
					let recallInfo: Array<dynamicObjectType> =
						res.data.recallInfo;
					let uaid = res.data.uaid;

					let productBatchName;

					if (product) {
						let imgs = product.images
								.filter((item: dynamicObjectType) => {
									//return item.isShow === 1;
									return item.displayFlag === true;
								})
								.map((item: dynamicObjectType) => {
									//return item.img;
									return item.imagePath;
								}),
							//fields = product.txt
							fields = product.attributes
								.filter((item: dynamicObjectType) => {
									// return item.isShow === 1;
									return item.displayFlag === true;
								})
								.map((item: dynamicObjectType) => {
									if (item.code === 'name') {
										//here special handlng to get the product batch name to use as product name.
										productBatchName = item.value;
									}
									return {
										//name: JSON.parse(item.name),
										//value: JSON.parse(item.value),
										name: item.name,
										value: item.value,
										valueType: item.valueType,
										fieldType: item.fieldType,
										code: item.code,
									};
								}); //,

						product = {
							name: product.productName,
							imgs: imgs,
							fields: fields,
							productBatchName: productBatchName,
							recallInfo: recallInfo,
						};

						const currentuaidlayer = uaid.layerNumberInSpecs;
						product['uaidType'] =
							uaid.specs.layers[currentuaidlayer - 1].type;
						product['currentReqUAID'] = uaid.uaid;
						product['batchNumber'] = uaid.uaidBatch.batchNumber;

						get().actions.setProduct(product);
						nav('/Product', { replace: true });
					}
				}
			},
		},
	})
);

export const useVerify = () => useBizLogicStore((state) => state.verify);
export const useProduct = () => useBizLogicStore((state) => state.product);
export const useBizLogicActions = () =>
	useBizLogicStore((state) => state.actions);

export default useBizLogicStore;
